import type { Feature, FeatureCollection, GeoJsonProperties, Geometry } from "geojson";
import { useAtomValue } from "jotai";
import { Suspense, useMemo } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { GeoJSON, useMap } from "react-leaflet";
import { selectedScenarioIdAtom } from "src/atoms/overlayers";
import { useSegments } from "src/hooks/segments/useSegments";
import wkx from "wkx";
import { feature as convertToFeature, featureCollection } from "@turf/helpers";

interface Props {
  opacity: number;
  pane: string;
  zIndex: number;
}
export function ScenarioStreetsWithBarrierOverlayer(props: Props) {
  const map = useMap();
  const scenarioId = useAtomValue(selectedScenarioIdAtom);

  if (!map) {
    console.warn("Can't render CaseBuffer overlayer when not inside <MapContainer />");
    return null;
  }

  if (!scenarioId) {
    console.warn("Can't render ScenarioStreetsWithBarrierOverlayer without a scenarioId");
    return null;
  }

  return (
    <ErrorBoundary fallback={null}>
      <Suspense fallback={null}>
        <LoadData {...props} scenarioId={scenarioId} />
      </Suspense>
    </ErrorBoundary>
  );
}

function LoadData(props: Props & { scenarioId: string }) {
  const { scenarioId, pane, opacity, zIndex } = props;
  const { data: segments } = useSegments(scenarioId);

  const filteredSegments = useMemo<FeatureCollection<Geometry, GeoJsonProperties>>(() => {
    const features = segments.reduce<Feature<Geometry, GeoJsonProperties>[]>((acc, segment) => {
      if (!segment.barrierHeight_m) return acc;

      const geometry = wkx.Geometry.parse(segment.lineString_frontendSrid).toGeoJSON() as Geometry;
      const feature = convertToFeature<Geometry, GeoJsonProperties>(geometry, {
        id: segment.segmentId,
      });

      acc.push(feature);
      return acc;
    }, []);

    return featureCollection<Geometry, GeoJsonProperties>(features);
  }, [segments]);

  return (
    <GeoJSON
      interactive={false}
      key={`barrier-segments-${props.pane}-${scenarioId}`}
      pane={pane}
      data={filteredSegments}
      // @ts-ignore works, but isn't typed...
      zIndex={zIndex}
      style={{ color: "white", opacity: opacity, weight: 4 }}
      pmIgnore={true}
    />
  );
}
